import type { ComponentType } from 'react';

import type { HopsEnvironmentExtension } from '@xing-com/crate-core-hops-environment';
import type { Host, Xinglet, XingletFunction } from '@xing-com/crate-xinglet';
import type { Runtime } from '@xing-com/crate-xinglet/internal';

import type { ConnectionStateService } from './fsm';
import { createStateService } from './fsm';
import { GetWebSocketAuthTokenDocument } from './websocket-token.gql-types';

export interface MessageHandler<T = unknown> {
  (data: T): void;
}

type OnConnectFunction = XingletFunction<
  WebSocketXinglet,
  '@xing-com/crate-core-websocket.onConnect'
>;
type ListenFunction = XingletFunction<
  WebSocketXinglet,
  '@xing-com/crate-core-websocket.listen'
>;
type SendFunction = XingletFunction<
  WebSocketXinglet,
  '@xing-com/crate-core-websocket.send'
>;

export interface WebSocketFunctions {
  executeCommand: OnConnectFunction | ListenFunction | SendFunction;
}

interface PhoenixReply<T> {
  joinRef: string | null;
  ref: string;
  topic: string;
  event: 'phx_reply';
  payload: T;
}

interface PhoenixHeartbeat {
  join_ref: null;
  topic: 'phoenix';
  event: 'heartbeat';
  payload: Record<string, unknown>;
}

interface PhoenixStatusReply {
  response: Record<string, unknown>;
  status: 'ok';
}

interface PhoenixJoin {
  join_ref: string;
  topic: string;
  event: 'phx_join';
  payload: Record<string, unknown>;
}

interface CustomMessage {
  join_ref: `${number}`;
  topic: `users:${string}`;
  event: string;
  payload: Record<string, unknown>;
}

type PhoenixMessages = PhoenixHeartbeat | PhoenixJoin | CustomMessage;

export default class WebSocketXinglet implements Xinglet {
  // This xinglet does not have a component, it just receives commands:
  getComponent?(host: Host, runtime: Runtime): ComponentType;

  #messageId = 1;
  #joinRef = 1;
  #websocket?: WebSocket;
  #waitForHeartbeat = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  #waitingForReply = new Map<string, MessageHandler<PhoenixReply<any>>>();

  #userId?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  #listeners = new Map<MessageHandler<any>, string>();

  #connectListener: (() => void)[] = [];

  #fsm: ConnectionStateService;

  get #connected(): boolean {
    return this.#fsm.state.matches('connected');
  }

  constructor(
    private host: Host<HopsEnvironmentExtension>,
    private WebSocketImpl?: typeof WebSocket
  ) {
    if (host.isServer) {
      this.#fsm = createStateService({
        connecting: () => Promise.reject(),
        disconnecting: () => Promise.reject(),
        joining: () => Promise.reject(),
      });

      return;
    }

    if (typeof WebSocket === 'function') {
      // start heartbeat only in clients with websocket support
      setInterval(() => {
        if (navigator.onLine) {
          this.#sendHeartbeat().catch((err) => {
            console.error('Failed to send heartbeat', err);
          });
        }
      }, 5000);
    }

    this.#fsm = createStateService({
      connecting: this.#createWebsocket.bind(this),
      disconnecting: this.#onClose.bind(this),
      joining: this.#join.bind(this),
    });

    window.addEventListener('online', () => this.#fsm.send('ONLINE'));
    window.addEventListener('offline', () => this.#fsm.send('OFFLINE'));
  }

  async #getIdAndToken(): Promise<{ id: string; token: string }> {
    const client = await this.host.executeCommand(
      '@xing-com/crate-core-hops-environment.apolloClient'
    );
    if (!client) {
      throw new Error('No graphql environment found');
    }

    const { data } = await client.query({
      query: GetWebSocketAuthTokenDocument,
      fetchPolicy: 'no-cache',
    });

    const id = data?.viewer?.id.replace(/\..*$/, '');
    if (!id) {
      throw new Error('Unable to get ID');
    }

    const token = data?.viewer?.networkHomeWebsocketAuthToken?.token;
    if (!token) {
      throw new Error('Unable to get AuthToken');
    }

    return {
      id,
      token,
    };
  }

  #getPort(protocol: 'wss:' | 'ws:'): string {
    if (window.location.port !== '') {
      return `:${window.location.port}`;
    }

    switch (protocol) {
      case 'ws:':
        return ':80';
      case 'wss:':
        return ':443';
    }
  }

  async #createWebsocket(): Promise<void> {
    const { id, token } = await this.#getIdAndToken();

    this.#userId = id;
    this.#messageId = 1;
    this.#joinRef = 1;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostname = this.host.getHostname('network-updates');
    const port = this.#getPort(protocol);
    const url = `${protocol}//${hostname}${port}/socket/websocket?token=${token}&user_id=${id}&client_type=web&vsn=2.0.0`;

    return new Promise<void>((resolve) => {
      this.#websocket = new (this.WebSocketImpl ?? WebSocket)(url);
      this.#websocket.binaryType = 'arraybuffer';
      this.#websocket.onopen = () => resolve();
      this.#websocket.onmessage = this.#onMessage.bind(this);
      this.#websocket.onclose = this.#onClose.bind(this);

      if (LOCAL_DEVELOPMENT) {
        // add option to operate/debug/test websocket connection
        // for local development
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (globalThis as any).__ws = this.#websocket;
      }
    });
  }

  async #join(): Promise<void> {
    try {
      await this.#sendJoin();

      setTimeout(() => {
        this.#connectListener.forEach((listener) => listener());
      }, 0);
    } catch {
      // this is a protocol error, restart connection...
      this.#websocket?.close(1000);
    }
  }

  async #connect(): Promise<void> {
    if (!this.#connected) {
      const untilConnected = new Promise<void>((resolve) => {
        const handle = this.#fsm.subscribe((state) => {
          if (state.matches('connected')) {
            handle.unsubscribe();
            resolve();
          }
        });
      });

      this.#fsm.send('CONNECT');

      await untilConnected;
    }
  }

  #onMessage(evt: MessageEvent<unknown>): void {
    if (!this.#userId) {
      return;
    }

    if (typeof evt.data !== 'string') {
      throw new Error(`Invalid event payload type '${typeof evt.data}'`);
    }

    const data = JSON.parse(evt.data);
    if (!Array.isArray(data)) {
      throw new Error(`Invalid event payload data type '${typeof data}'`);
    }

    const [joinRef, ref, topic, event, payload] = data as unknown[];
    if (joinRef !== null && typeof joinRef !== 'string') {
      throw new Error(`Invalid event reply join_ref type '${typeof joinRef}'`);
    }
    if (typeof topic !== 'string') {
      throw new Error(`Invalid event reply topic type '${typeof topic}'`);
    }
    if (typeof event !== 'string') {
      throw new Error(`Invalid event reply event type '${typeof event}'`);
    }

    if (event === 'phx_reply' && typeof ref === 'string') {
      const message: PhoenixReply<unknown> = {
        joinRef,
        ref,
        topic,
        event,
        payload,
      };

      const handler = this.#waitingForReply.get(ref);
      if (handler) {
        this.#waitingForReply.delete(ref);
        handler(message);
      }
    } else if (topic === this.#getTopic()) {
      const listeners = this.#listeners.entries();
      for (const [handler, messageType] of listeners) {
        if (event === messageType) {
          handler(payload);
        }
      }
    }
  }

  async #onClose(evt?: CloseEvent): Promise<void> {
    if (evt) {
      console.debug('[ws] close event', evt.code, evt.reason);
    } else {
      console.debug('[ws] close');
    }
    this.#websocket = undefined;

    if (navigator.onLine) {
      const stateEvent = this.#listeners.size > 0 ? 'ERROR' : 'DISCONNECT';
      this.#fsm.send(stateEvent);
    }
  }

  async #sendHeartbeat(): Promise<void> {
    if (!this.#websocket) {
      return;
    }

    if (!this.#connected) {
      return;
    }

    if (this.#waitForHeartbeat) {
      // this is a protocol error, restart connection...
      this.#waitForHeartbeat = false;
      this.#websocket.close(1000);
      return;
    }

    this.#waitForHeartbeat = true;
    const reply = await this.#sendMessage<PhoenixStatusReply>({
      join_ref: null,
      topic: 'phoenix',
      event: 'heartbeat',
      payload: {},
    } satisfies PhoenixHeartbeat);
    this.#waitForHeartbeat = false;

    if (reply.payload.status !== 'ok') {
      // this is a protocol error, restart connection...
      this.#websocket.close(1000);
    }
  }

  async #sendJoin(): Promise<number> {
    if (!this.#websocket) {
      throw new Error('Unable to join channel without connect');
    }
    if (!this.#userId) {
      throw new Error('Unable to join channel without user ID');
    }

    const ref = this.#joinRef;

    const reply = await this.#sendMessage<PhoenixStatusReply>({
      join_ref: `${ref}`,
      topic: this.#getTopic(),
      event: 'phx_join',
      payload: {},
    } satisfies PhoenixJoin);

    if (reply.payload.status !== 'ok') {
      throw new Error(`Failed to join channel 'users:${this.#userId}'`);
    }

    return ref;
  }

  #getTopic(): string {
    return this.#userId === '0'
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        `users:${globalThis.crate.visitorId}`
      : `users:${this.#userId}`;
  }

  /**
   * This is a low-level function without any error handling or queueing.
   */

  async #sendMessage<T>(message: PhoenixMessages): Promise<PhoenixReply<T>> {
    if (!this.#websocket) {
      throw new Error(`WebSocket not available and no queueing implemented`);
    }

    const messageId = `${this.#messageId++}`;
    let resolve: undefined | MessageHandler<PhoenixReply<T>>;
    const promise = new Promise<PhoenixReply<T>>(
      (_resolve) => (resolve = _resolve)
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.#waitingForReply.set(messageId, resolve!);

    const { join_ref, topic, event, payload } = message;
    const data = [join_ref, messageId, topic, event, payload];
    this.#websocket.send(JSON.stringify(data));

    return promise;
  }

  public async '@xing-com/crate-core-websocket.onConnect'(
    callback: () => void
  ): Promise<() => void> {
    this.#connectListener.push(callback);
    if (!this.#connected) {
      await this.#connect();
    } else {
      setTimeout(callback, 0);
    }

    return () => {
      const index = this.#connectListener.findIndex(
        (listener) => listener === callback
      );
      if (index > -1) {
        this.#connectListener.splice(index, 1);
      }
    };
  }

  /**
   * @param channelName The channel to join
   * @param messageType The message type to listen for
   * @param callback A function to call when a new message is received
   * @returns a function which unsubscribes when called
   */
  public async '@xing-com/crate-core-websocket.listen'<T extends string>(
    messageType: T,
    callback: MessageHandler<{ type: T }>
  ): Promise<() => void> {
    this.#listeners.set(callback, messageType);
    await this.#connect();

    return () => {
      this.#listeners.delete(callback);
    };
  }

  public async '@xing-com/crate-core-websocket.send'(
    type: string,
    message: Record<string, unknown>
  ): Promise<void> {
    await this.#connect();

    if (!this.#userId) {
      return;
    }

    const reply = await this.#sendMessage<PhoenixStatusReply>({
      join_ref: `${this.#joinRef}`,
      topic: `users:${this.#userId}`,
      event: type,
      payload: message,
    } satisfies CustomMessage);

    if (reply.payload.status !== 'ok') {
      throw new Error(`Failed to send message: ${reply.payload.response}`);
    }
  }
}
